/* Hooks TanStack Query do FAQ/capacidades (ADR-0658 + ADR-0720). A lista é PAGINADA POR TÓPICO
   (keyset) via `useInfiniteQuery`. A gestão é POR-TÓPICO (slug): o save faz upsert por (slug,locale)
   para cada idioma preenchido e o delete remove todas as linhas do slug — ambos reusam os endpoints
   por-locale existentes (create/update/delete), um por idioma. `categoryKey` é compartilhado pelo
   tópico; `categoryLabel` é por idioma. */
import type { CreateFaqBody, FaqStatus, FaqTopic, UpdateFaqBody } from '@planuze/admin-contracts';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  bulkDeleteFaqTopics,
  createFaq,
  deleteFaq,
  listFaqTopics,
  updateFaq,
} from '../lib/faq-api.js';

const FAQ_LIST_KEY = ['faq', 'list'] as const;

interface FaqTopicsPage {
  readonly items: readonly FaqTopic[];
  readonly nextCursor: string | null;
}

/* Lista paginada por tópico (ADR-0720): keyset via cursor opaco; `getNextPageParam` lê o
   `nextCursor` da última página (undefined quando acaba). `categoryKey` opcional (ADR-0728) filtra
   server-side — surfaces dedicadas (ex.: guia do publisher) listam só a sua categoria. */
export function useFaqTopics(categoryKey?: string) {
  return useInfiniteQuery({
    queryKey: categoryKey !== undefined ? [...FAQ_LIST_KEY, categoryKey] : FAQ_LIST_KEY,
    queryFn: ({ pageParam }) =>
      listFaqTopics({ cursor: pageParam, ...(categoryKey !== undefined && { categoryKey }) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

function useInvalidateFaq(): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: FAQ_LIST_KEY });
  };
}

/** Uma variação de idioma a persistir no save do tópico. `existingId` presente = update
    (linha já existe nesse locale); ausente = create (novo (slug,locale)). `categoryLabel` é o
    rótulo da categoria NAQUELE idioma. */
export interface SaveFaqTopicLocale {
  readonly locale: string;
  readonly categoryLabel: string;
  readonly question: string;
  readonly answer: string;
  readonly status: FaqStatus;
  readonly existingId?: string;
}
/* Input do save por-tópico: metadados compartilhados (slug/categoryKey/ordem) + as variações de
   idioma preenchidas (cada uma com seu categoryLabel). */
interface SaveFaqTopicInput {
  readonly slug: string;
  readonly categoryKey: string;
  readonly sortOrder: number;
  readonly locales: readonly SaveFaqTopicLocale[];
}

/* Salva um TÓPICO: upsert por (slug,locale) para cada idioma preenchido (um request por locale).
   slug/categoryKey/ordem são compartilhados; pergunta/resposta/status/categoryLabel são por idioma.
   Sequencial para erro determinístico; invalida a lista no onSettled (reconcilia writes parciais em
   erro). */
export function useSaveFaqTopic() {
  const invalidate = useInvalidateFaq();
  return useMutation({
    mutationFn: async (input: SaveFaqTopicInput) => {
      for (const entry of input.locales) {
        if (entry.existingId !== undefined) {
          const body: UpdateFaqBody = {
            categoryKey: input.categoryKey,
            categoryLabel: entry.categoryLabel,
            question: entry.question,
            answer: entry.answer,
            locale: entry.locale,
            status: entry.status,
            sortOrder: input.sortOrder,
          };
          await updateFaq(entry.existingId, body);
        } else {
          const body: CreateFaqBody = {
            slug: input.slug,
            categoryKey: input.categoryKey,
            categoryLabel: entry.categoryLabel,
            question: entry.question,
            answer: entry.answer,
            locale: entry.locale,
            status: entry.status,
            sortOrder: input.sortOrder,
          };
          await createFaq(body);
        }
      }
    },
    onSettled: invalidate,
  });
}

/* Remove um TÓPICO inteiro: deleta a linha de cada idioma (um DELETE por locale). Otimista
   (ADR-0699 + ADR-0720): tira o tópico (por slug) de TODAS as páginas do cache infinito no mesmo
   frame (setQueryData), reconcilia no onSettled e faz rollback do snapshot no erro. */
export function useDeleteFaqTopic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { readonly slug: string; readonly ids: readonly string[] }) =>
      Promise.all(ids.map((id) => deleteFaq(id))).then(() => undefined),
    onMutate: async ({ slug }: { readonly slug: string; readonly ids: readonly string[] }) => {
      await queryClient.cancelQueries({ queryKey: FAQ_LIST_KEY });
      const previous = queryClient.getQueryData<InfiniteData<FaqTopicsPage>>(FAQ_LIST_KEY);
      queryClient.setQueryData<InfiniteData<FaqTopicsPage>>(FAQ_LIST_KEY, (old) =>
        old === undefined
          ? old
          : {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: page.items.filter((topic) => topic.slug !== slug),
              })),
            },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(FAQ_LIST_KEY, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: FAQ_LIST_KEY }),
  });
}

export function useBulkDeleteFaqTopics() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slugs: readonly string[]) => bulkDeleteFaqTopics(slugs),
    onSettled: () => queryClient.invalidateQueries({ queryKey: FAQ_LIST_KEY }),
  });
}
